import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const WorkflowBreadcrumb = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const steps = [
    { label: 'Setup', route: '/location-input' },
    { label: 'Results', route: '/meeting-point-results' },
    { label: 'Refine', route: '/location-type-filter' },
    { label: 'Finalize', route: '/final-meeting-details' }
  ];

  const currentStepIndex = steps?.findIndex(step => step?.route === location?.pathname);
  const currentStep = steps?.[currentStepIndex];
  const completedSteps = steps?.slice(0, currentStepIndex);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleStepClick = (route) => {
    navigate(route);
    setIsDropdownOpen(false);
  };

  if (!currentStep) return null;

  return (
    <nav aria-label="Breadcrumb" className="bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="hidden md:flex items-center gap-2 text-sm">
          <a
            href="/"
            className="text-muted-foreground hover:text-foreground transition-smooth flex items-center gap-1"
          >
            <Icon name="Home" size={16} />
            <span>Home</span>
          </a>
          {completedSteps?.map((step, index) => (
            <React.Fragment key={step?.route}>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              <button
                onClick={() => handleStepClick(step?.route)}
                className="text-muted-foreground hover:text-foreground transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-1"
              >
                {step?.label}
              </button>
            </React.Fragment>
          ))}
          <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
          <span className="text-foreground font-medium">{currentStep?.label}</span>
        </div>

        <div className="md:hidden relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 text-sm text-foreground font-medium w-full justify-between py-2 px-3 rounded-lg hover:bg-muted transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
          >
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={16} className="text-primary" />
              <span>{currentStep?.label}</span>
            </div>
            <Icon 
              name={isDropdownOpen ? "ChevronUp" : "ChevronDown"} 
              size={16} 
              className="text-muted-foreground"
            />
          </button>

          {isDropdownOpen && completedSteps?.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden animate-slide-in">
              <div className="py-2">
                <a
                  href="/"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
                >
                  <Icon name="Home" size={16} />
                  <span>Home</span>
                </a>
                {completedSteps?.map((step) => (
                  <button
                    key={step?.route}
                    onClick={() => handleStepClick(step?.route)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth w-full text-left"
                  >
                    <Icon name="Check" size={16} className="text-success" />
                    <span>{step?.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default WorkflowBreadcrumb;